import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  DashboardRole,
  getCouponWindfallCohort,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  WindfallCohortBucket,
  WindfallCohortRow,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import { fmtPercent } from '../utils/dashboard.utils';
import { CenterLoading, ErrorBox, StatCard } from './DashboardShared';

/**
 * 낙전 코호트 — 판매월별로 이후 월마다 발송/낙전환불로 몇 건이 소진되고
 * 잔존(=낙전 예상)이 어떻게 줄어드는지 보여준다. 쿠폰 전용.
 * 스펙: docs/gift-coupon-windfall-cohort-spec.md
 */

const isNotFound = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;

const fmtWon = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const fmtMan = (n: number) => `${Math.round(n / 10_000).toLocaleString('ko-KR')}만`;

type Axis = 'count' | 'amount';
type RangeMonths = 6 | 12 | 24 | 0; // 0 = 전체

// 매트릭스 열은 +0~+13 고정(만료월 +12 뒤에 처리되는 지연 환불까지 한 칸 여유).
// 아직 안 지난 달도 미리 영역을 잡아두고(미도래 ·) 가로 스크롤로 본다.
const MATRIX_OFFSETS = 14;

const RANGE_OPTIONS: Array<{ value: RangeMonths; label: string }> = [
  { value: 6, label: '최근 6개월' },
  { value: 12, label: '최근 12개월' },
  { value: 24, label: '최근 24개월' },
  { value: 0, label: '전체' },
];

/** KST 기준 현재 연·월 */
function kstNowParts(): { y: number; m: number } {
  const t = new Date(Date.now() + 9 * 3_600_000);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 };
}

/**
 * 최근 N개월(현재 월 포함) 판매월 범위를 KST 경계로 계산해 UTC(Z) ISO로 변환.
 * queryString이 encode:false라 '+09:00'을 그대로 보내면 서버가 '+'를 공백으로
 * 디코딩해 Invalid Date가 된다 — 반드시 toISOString()으로 변환해 보낼 것.
 */
function buildCohortRange(months: number): { from: string; to: string } {
  const { y, m } = kstNowParts();
  const startTotal = y * 12 + (m - 1) - (months - 1);
  const sy = Math.floor(startTotal / 12);
  const sm = (startTotal % 12) + 1;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    from: new Date(`${sy}-${pad(sm)}-01T00:00:00+09:00`).toISOString(),
    to: new Date(`${y}-${pad(m)}-${pad(lastDay)}T23:59:59.999+09:00`).toISOString(),
  };
}

/** 'YYYY-MM' + n개월 */
function addMonthKey(key: string, n: number): string {
  const [y, m] = key.split('-').map(Number);
  const total = (y ?? 0) * 12 + ((m ?? 1) - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/** 'YYYY-MM' → 그 월말 일자 라벨 'YY.MM.DD' (매트릭스 셀 시점 표기) */
function monthEndLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  const yy = y ?? 0;
  const mm = m ?? 1;
  const lastDay = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  return `${String(yy).slice(2)}.${String(mm).padStart(2, '0')}.${String(
    lastDay,
  ).padStart(2, '0')}`;
}

function remainRatio(
  row: WindfallCohortRow,
  b: WindfallCohortBucket,
  axis: Axis,
): number {
  const den = axis === 'count' ? row.total : row.totalAmount;
  const num = axis === 'count' ? b.remaining : b.remainingAmount;
  return den > 0 ? num / den : 0;
}

export const DashboardWindfallCohortSection: FC<{ role?: DashboardRole }> = ({
  role,
}) => {
  const [rows, setRows] = useState<WindfallCohortRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false);
  const [rangeMonths, setRangeMonths] = useState<RangeMonths>(12);
  const [axis, setAxis] = useState<Axis>('count');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      const range = rangeMonths > 0 ? buildCohortRange(rangeMonths) : null;
      const res = await getCouponWindfallCohort(range?.from, range?.to, role);
      const cohorts = res.cohorts ?? [];
      setRows(cohorts);
      // 기본 선택: 가장 최근 판매월
      setSelectedMonth(cohorts[cohorts.length - 1]?.cohortMonth ?? null);
    } catch (e: unknown) {
      if (isNotFound(e)) {
        setNotReady(true);
      } else {
        setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
      }
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [rangeMonths, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selected = useMemo(
    () => (rows ?? []).find((r) => r.cohortMonth === selectedMonth) ?? null,
    [rows, selectedMonth],
  );

  return (
    <section className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">월별 코호트 추이 (쿠폰)</h2>
          <p className="mt-1 text-xs text-gray-400">
            판매월별로 이후 월마다 발송·낙전환불로 몇 건이 소진되고, 잔존(=낙전 예상)이
            어떻게 줄어드는지 봅니다. 취소·폐기·추가발행분은 모수에서 제외됩니다.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            {RANGE_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setRangeMonths(o.value)}
                className={`rounded px-3 py-1 transition-colors ${
                  rangeMonths === o.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="flex rounded-md bg-gray-200 p-0.5 text-xs font-semibold">
            {(['count', 'amount'] as const).map((a) => (
              <button
                key={a}
                onClick={() => setAxis(a)}
                className={`rounded px-3 py-1 transition-colors ${
                  axis === a
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {a === 'count' ? '건수' : '금액'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {notReady && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-700">
          낙전 코호트 API가 아직 이 서버에 배포되지 않았습니다. 백엔드 배포 후 이용할 수
          있습니다.
        </div>
      )}
      {loading && <CenterLoading />}
      {error && <ErrorBox message={error} onRetry={fetchData} />}
      {!loading && !error && !notReady && rows && rows.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-400">
          해당 기간에 판매된 쿠폰이 없습니다.
        </div>
      )}

      {!loading && !error && !notReady && rows && rows.length > 0 && (
        <>
          {/* 코호트 매트릭스 */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500">
                  <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold">
                    판매월
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">모수</th>
                  <th className="px-3 py-2 text-right font-semibold">현재 잔존</th>
                  {Array.from({ length: MATRIX_OFFSETS }, (_, i) => (
                    <th key={i} className="px-2 py-2 text-center font-semibold">
                      +{i}개월
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isSelected = r.cohortMonth === selectedMonth;
                  const currentRatio =
                    (axis === 'count' ? r.total : r.totalAmount) > 0
                      ? (axis === 'count' ? r.remaining : r.remainingAmount) /
                        (axis === 'count' ? r.total : r.totalAmount)
                      : 0;
                  return (
                    <tr
                      key={r.cohortMonth}
                      onClick={() => setSelectedMonth(r.cohortMonth)}
                      className={`cursor-pointer border-t border-gray-100 text-sm transition-colors ${
                        isSelected ? 'bg-indigo-50/60' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td
                        className={`sticky left-0 z-10 whitespace-nowrap px-3 py-2 font-semibold ${
                          isSelected
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'bg-white text-gray-700'
                        }`}
                      >
                        {r.cohortMonth}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-gray-700">
                        {axis === 'count'
                          ? `${r.total.toLocaleString('ko-KR')}건`
                          : fmtMan(r.totalAmount)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-amber-700">
                        {axis === 'count'
                          ? `${r.remaining.toLocaleString('ko-KR')}건`
                          : fmtMan(r.remainingAmount)}
                        <span className="ml-1 text-xs font-normal text-amber-500">
                          {fmtPercent(currentRatio)}
                        </span>
                      </td>
                      {Array.from({ length: MATRIX_OFFSETS }, (_, i) => {
                        const b = r.buckets[i];
                        // 판매월이 행마다 달라 헤더에 못 박는 실제 시점(월말 일자) — 셀 위에 표기
                        const cellDateLabel = monthEndLabel(
                          addMonthKey(r.cohortMonth, i),
                        );
                        if (!b) {
                          // 미도래(아직 안 지난 달) — 영역만 미리 잡아둔다
                          return (
                            <td key={i} className="bg-gray-50/60 px-2 py-2 text-center">
                              <div className="text-[10px] text-gray-400">
                                {cellDateLabel}
                              </div>
                              <div className="text-xs text-gray-300">·</div>
                            </td>
                          );
                        }
                        const ratio = remainRatio(r, b, axis);
                        const isExpiry = b.month === r.expiresMonth;
                        const prev = i === 0 ? undefined : r.buckets[i - 1];
                        // 소진 분해: 발송 vs 낙전환불. 금액 축의 환불분은
                        // 잔존액 차감분(판매가 축)이라 발송%+환불% = 그 달 잔존율 감소분
                        const den = axis === 'count' ? r.total : r.totalAmount;
                        const deliveredOut =
                          axis === 'count' ? b.delivered : b.deliveredAmount;
                        const refundedOut =
                          axis === 'count'
                            ? b.refunded
                            : Math.max(
                                0,
                                (prev?.remainingAmount ?? r.totalAmount) -
                                  b.remainingAmount -
                                  b.deliveredAmount,
                              );
                        const deliveredPct = den > 0 ? deliveredOut / den : 0;
                        const refundedPct = den > 0 ? refundedOut / den : 0;
                        return (
                          <td
                            key={i}
                            title={`${b.month} — 발송 ${b.delivered}건 · 낙전환불 ${b.refunded}건 · 월말 잔존 ${b.remaining}건`}
                            className={`whitespace-nowrap px-2 py-2 text-center ${
                              isExpiry ? 'ring-1 ring-inset ring-red-200' : ''
                            }`}
                            style={{
                              backgroundColor: `rgba(129, 140, 248, ${(
                                ratio * 0.55
                              ).toFixed(3)})`,
                            }}
                          >
                            <div className="text-[10px] font-semibold text-gray-800">
                              {cellDateLabel}
                            </div>
                            <div className="flex items-center justify-center gap-1 text-xs font-bold">
                              {deliveredOut > 0 && (
                                <span className="text-emerald-600">
                                  ▼{fmtPercent(deliveredPct)}
                                </span>
                              )}
                              {refundedOut > 0 && (
                                <span className="text-amber-600">
                                  ▼{fmtPercent(refundedPct)}
                                </span>
                              )}
                              {deliveredOut === 0 && refundedOut === 0 && (
                                <span className="font-normal text-gray-300">·</span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-600">
                              {axis === 'count'
                                ? b.remaining.toLocaleString('ko-KR')
                                : fmtMan(b.remainingAmount)}
                              <span className="ml-0.5 text-gray-400">
                                ({fmtPercent(ratio)})
                              </span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400">
            <span>
              셀 상단 <span className="font-semibold text-gray-800">날짜</span> = 그 월말
              스냅샷 시점, 가운데 = 그 달 빠진 비율(모수 대비,{' '}
              <span className="font-bold text-emerald-600">▼발송</span>{' '}
              <span className="font-bold text-amber-600">▼낙전환불</span>), 아래 = 월말
              잔존(잔존율). 배경이 짙을수록 잔존↑, 붉은 테두리 = 유효기간 만료월(구매
              +1년), · = 미도래. 행을 클릭하면 아래에 월별 상세가 열립니다.
            </span>
          </div>

          {/* 선택 코호트 상세 */}
          {selected && (
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                {selected.cohortMonth} 판매분 상세
                <span className="ml-2 text-xs font-normal text-gray-400">
                  유효기간 만료: {selected.expiresMonth}
                </span>
              </h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <StatCard
                  label="모수 (판매)"
                  value={`${selected.total.toLocaleString('ko-KR')}건`}
                  sub={fmtWon(selected.totalAmount)}
                  accent="indigo"
                />
                <StatCard
                  label="발송(사용) 누적"
                  value={`${selected.deliveredTotal.toLocaleString('ko-KR')}건`}
                  sub={fmtWon(selected.deliveredTotalAmount)}
                  accent="emerald"
                />
                <StatCard
                  label="낙전환불 누적"
                  value={`${selected.refundedTotal.toLocaleString('ko-KR')}건`}
                  sub={`실환불 ${fmtWon(selected.refundedTotalAmount)}`}
                  accent="red"
                />
                <StatCard
                  label="현재 잔존 (낙전 예상)"
                  value={`${selected.remaining.toLocaleString('ko-KR')}건`}
                  sub={fmtWon(selected.remainingAmount)}
                  accent="orange"
                />
              </div>
              {selected.refundedAfterDelivery > 0 && (
                <p className="mt-2 text-xs text-gray-400">
                  발송 후 환불 {selected.refundedAfterDelivery}건 (실환불{' '}
                  {fmtWon(selected.refundedAfterDeliveryAmount)}) — 발송 누적에 이미
                  포함되어 있어 잔존에서 재차감하지 않습니다.
                </p>
              )}

              <CohortDecayChart row={selected} />
            </div>
          )}
        </>
      )}
    </section>
  );
};

// 잔존 라인·소진 막대 색 — dataviz 검증 통과 조합(emerald-600/amber-600/indigo-600,
// surface gray-50). emerald↔amber는 CVD 경계값이라 고정 스택 순서+표면 갭이 보조 인코딩.
const C_DELIVERED = '#059669';
const C_REFUNDED = '#d97706';
const C_REMAINING = '#4f46e5';
const C_SURFACE = '#f9fafb'; // gray-50 — 마커 링·세그먼트 갭용
const C_GRID = '#e5e7eb'; // gray-200
const C_BASELINE = '#d1d5db'; // gray-300

/** 1/2/5 스텝으로 눈금 간격을 잡는다 */
function niceStep(raw: number): number {
  const pow = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  const n = raw / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow;
}

/** 위쪽만 둥근 막대 path (데이터 끝 4px 라운드, 베이스라인은 각지게) */
function topRoundedRect(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, h, w / 2);
  return [
    `M${x},${y + h}`,
    `L${x},${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `L${x + w - r},${y}`,
    `Q${x + w},${y} ${x + w},${y + r}`,
    `L${x + w},${y + h}`,
    'Z',
  ].join(' ');
}

/**
 * 잔존 꺾은선(stock) + 월별 소진 증분 막대(flow) 콤보.
 * 선은 모수에서 시작해 월말 잔존으로 내려가고 — 평평해지는 높이가 낙전 예상 —
 * 막대는 그 달에 빠진 발송/낙전환불 건수라 선의 계단 낙폭과 일치한다.
 * 정확한 수치는 위 매트릭스 표가 테이블 뷰 역할을 한다. 건수 축 고정.
 */
const CohortDecayChart: FC<{ row: WindfallCohortRow }> = ({ row }) => {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (row.total === 0 || row.buckets.length === 0) return null;

  // 슬롯 0 = 판매 시작점(선만), 1..N = 월 버킷(막대+선)
  const slots = row.buckets.length + 1;
  const SLOT_W = Math.min(Math.max(760 / slots, 40), 72);
  const PAD_L = 44;
  const PAD_R = 12;
  const PAD_T = 26; // 포인트 라벨 헤드룸
  const PLOT_H = 180;
  const AXIS_H = 26;
  const chartW = PAD_L + slots * SLOT_W + PAD_R;
  const chartH = PAD_T + PLOT_H + AXIS_H;
  const baselineY = PAD_T + PLOT_H;

  const yOf = (v: number) => baselineY - (v / row.total) * PLOT_H;
  const slotCenter = (i: number) => PAD_L + (i + 0.5) * SLOT_W;
  const barW = Math.min(24, SLOT_W * 0.5);

  const tickStep = niceStep(row.total / 4);
  const ticks: number[] = [];
  for (let v = 0; v <= row.total; v += tickStep) ticks.push(v);

  // 선 좌표: 시작점(모수) + 각 월말 잔존
  const points = [
    { x: slotCenter(0), y: yOf(row.total), value: row.total },
    ...row.buckets.map((b, i) => ({
      x: slotCenter(i + 1),
      y: yOf(b.remaining),
      value: b.remaining,
    })),
  ];
  // 직접 라벨은 선별적으로 — 시작점, 값이 바뀐 포인트, 마지막 포인트만
  const labeled = points.map(
    (p, i) => i === 0 || i === points.length - 1 || p.value !== points[i - 1]?.value,
  );

  const expiryBucketIdx = row.buckets.findIndex((b) => b.month === row.expiresMonth);
  const hovered = hoverIdx === null ? null : hoverIdx === 0 ? null : hoverIdx;
  const hoveredBucket = hovered === null ? undefined : row.buckets[hovered - 1];

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="overflow-x-auto">
        <div className="relative w-max min-w-full">
          <svg
            width={chartW}
            height={chartH}
            className="block"
            role="img"
            aria-label={`${row.cohortMonth} 판매분 잔존 추이 — 모수 ${row.total}건, 현재 잔존 ${row.remaining}건`}
          >
            {/* 그리드 + Y눈금 */}
            {ticks.map((v) => (
              <g key={v}>
                <line
                  x1={PAD_L}
                  x2={chartW - PAD_R}
                  y1={yOf(v)}
                  y2={yOf(v)}
                  stroke={v === 0 ? C_BASELINE : C_GRID}
                  strokeWidth={1}
                />
                <text
                  x={PAD_L - 6}
                  y={yOf(v) + 3}
                  textAnchor="end"
                  className="fill-gray-400 text-[10px] tabular-nums"
                >
                  {v.toLocaleString('ko-KR')}
                </text>
              </g>
            ))}

            {/* hover 밴드 — 마크 응답 겸 크로스헤어 */}
            {hoverIdx !== null && (
              <rect
                x={PAD_L + hoverIdx * SLOT_W}
                y={0}
                width={SLOT_W}
                height={baselineY}
                rx={4}
                fill="#e5e7eb"
                opacity={0.45}
              />
            )}

            {/* 만료월 세로 점선(이벤트 마커) */}
            {expiryBucketIdx >= 0 && (
              <line
                x1={slotCenter(expiryBucketIdx + 1)}
                x2={slotCenter(expiryBucketIdx + 1)}
                y1={PAD_T - 8}
                y2={baselineY}
                stroke="#f87171"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            )}

            {/* 월별 소진 증분 막대 — 발송(아래) + 낙전환불(위), 2px 표면 갭 */}
            {row.buckets.map((b, i) => {
              const x = slotCenter(i + 1) - barW / 2;
              const hD =
                b.delivered > 0 ? Math.max((b.delivered / row.total) * PLOT_H, 2) : 0;
              const hR =
                b.refunded > 0 ? Math.max((b.refunded / row.total) * PLOT_H, 2) : 0;
              const gap = hD > 0 && hR > 0 ? 2 : 0;
              return (
                <g key={b.month}>
                  {hD > 0 &&
                    (hR > 0 ? (
                      <rect
                        x={x}
                        y={baselineY - hD}
                        width={barW}
                        height={hD - gap / 2}
                        fill={C_DELIVERED}
                      />
                    ) : (
                      <path
                        d={topRoundedRect(x, baselineY - hD, barW, hD)}
                        fill={C_DELIVERED}
                      />
                    ))}
                  {hR > 0 && (
                    <path
                      d={topRoundedRect(x, baselineY - hD - gap - hR, barW, hR)}
                      fill={C_REFUNDED}
                    />
                  )}
                </g>
              );
            })}

            {/* 잔존 꺾은선 + 마커(표면색 링) */}
            <polyline
              points={points.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke={C_REMAINING}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {points.map((p, i) => (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={4}
                  fill={C_REMAINING}
                  stroke={C_SURFACE}
                  strokeWidth={2}
                />
                {labeled[i] && (
                  // 표면색 헤일로 — 라벨이 막대 위에 얹혀도(만료월 환불 스파이크 등) 읽히게
                  <text
                    x={p.x}
                    y={p.y - 9}
                    textAnchor="middle"
                    stroke={C_SURFACE}
                    strokeWidth={3}
                    paintOrder="stroke"
                    className="fill-gray-700 text-[11px] font-semibold tabular-nums"
                  >
                    {p.value.toLocaleString('ko-KR')}
                  </text>
                )}
              </g>
            ))}

            {/* X축 라벨 */}
            {row.buckets.map((b, i) => {
              const isExpiry = i === expiryBucketIdx;
              return (
                <text
                  key={b.month}
                  x={slotCenter(i + 1)}
                  y={baselineY + 16}
                  textAnchor="middle"
                  className={
                    isExpiry
                      ? 'fill-red-500 text-[11px] font-bold'
                      : 'fill-gray-400 text-[11px]'
                  }
                >
                  {b.month.slice(2)}
                  {isExpiry ? ' 만료' : ''}
                </text>
              );
            })}
            <text
              x={slotCenter(0)}
              y={baselineY + 16}
              textAnchor="middle"
              className="fill-gray-400 text-[11px]"
            >
              판매
            </text>

            {/* 슬롯 단위 히트 영역 — hover/키보드 포커스 공용 */}
            {Array.from({ length: slots }, (_, i) => {
              const b = i === 0 ? undefined : row.buckets[i - 1];
              const label =
                i === 0
                  ? `판매 시점 — 모수 ${row.total.toLocaleString('ko-KR')}건`
                  : b
                  ? `${b.month} — 월말 잔존 ${b.remaining}건 (${fmtPercent(
                      b.remaining / row.total,
                    )}) · 발송 ${b.delivered}건 · 낙전환불 ${b.refunded}건`
                  : '';
              return (
                <rect
                  key={i}
                  x={PAD_L + i * SLOT_W}
                  y={0}
                  width={SLOT_W}
                  height={chartH}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={label}
                  className="outline-none"
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  onFocus={() => setHoverIdx(i)}
                  onBlur={() => setHoverIdx(null)}
                />
              );
            })}
          </svg>

          {/* 툴팁 — hover 슬롯 옆(왼쪽 절반이면 오른쪽에, 오른쪽 절반이면 왼쪽에) */}
          {hoverIdx !== null && (
            <div
              className="pointer-events-none absolute top-1 z-10 whitespace-nowrap rounded-md bg-gray-800 px-3 py-2 text-xs text-white shadow-lg"
              style={
                slotCenter(hoverIdx) < chartW / 2
                  ? { left: slotCenter(hoverIdx) + SLOT_W / 2 + 8 }
                  : { right: chartW - slotCenter(hoverIdx) + SLOT_W / 2 + 8 }
              }
            >
              {hoverIdx === 0 || !hoveredBucket ? (
                <>
                  <div className="mb-1 font-semibold">{row.cohortMonth} 판매</div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-0.5 w-3 rounded"
                      style={{ backgroundColor: '#818cf8' }}
                    />
                    <span className="font-bold tabular-nums">
                      {row.total.toLocaleString('ko-KR')}건
                    </span>
                    <span className="text-gray-300">모수</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-1 font-semibold">
                    {hoveredBucket.month}
                    {hovered !== null && hovered - 1 === expiryBucketIdx && (
                      <span className="ml-1 text-red-300">만료월</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-0.5 w-3 rounded"
                      style={{ backgroundColor: '#818cf8' }}
                    />
                    <span className="font-bold tabular-nums">
                      {hoveredBucket.remaining.toLocaleString('ko-KR')}건 (
                      {fmtPercent(hoveredBucket.remaining / row.total)})
                    </span>
                    <span className="text-gray-300">월말 잔존</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 rounded-sm"
                      style={{ backgroundColor: '#34d399' }}
                    />
                    <span className="font-bold tabular-nums">
                      {hoveredBucket.delivered.toLocaleString('ko-KR')}건
                    </span>
                    <span className="text-gray-300">발송</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 rounded-sm"
                      style={{ backgroundColor: '#fbbf24' }}
                    />
                    <span className="font-bold tabular-nums">
                      {hoveredBucket.refunded.toLocaleString('ko-KR')}건
                    </span>
                    <span className="text-gray-300">낙전환불</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs font-medium">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: C_DELIVERED }}
          />
          <span className="text-gray-600">그 달 발송(사용)</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ backgroundColor: C_REFUNDED }}
          />
          <span className="text-gray-600">그 달 낙전환불</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="relative inline-block h-0.5 w-4 rounded"
            style={{ backgroundColor: C_REMAINING }}
          >
            <span
              className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                backgroundColor: C_REMAINING,
                boxShadow: `0 0 0 1.5px ${C_SURFACE}`,
              }}
            />
          </span>
          <span className="text-gray-600">월말 잔존 (낙전 예상)</span>
        </span>
        <span className="ml-auto text-gray-400">
          선이 평평해지는 높이 = 낙전 예상 · 막대 = 그 달 소진량 (건수 기준)
        </span>
      </div>
    </div>
  );
};
